import { useCallback, useEffect, useRef, useState } from 'react';
import { useHeaderCondense, useReveal } from '../ui/motion';
import { Mark } from '../ui/Brand';
import { Icon, type IconName } from '../ui/Icon';
import { Reports } from './Reports';
import { Report } from './Report';
import { Wallets } from './Wallets';
import { Chains } from './Chains';
import { Compare } from './Compare';
import { loadData, resolveWindow } from '../api/client';
import type { Payload, WindowKey } from '../api/types';
import { Dashboard } from './Dashboard';
import { Casinos } from './Casinos';
import { CasinoProfile } from './CasinoProfile';
import { Sources } from './Sources';

/** Pages in the header. The profile route exists but is reached from a casino, not the nav. */
const NAV = [
  { path: '/', label: 'Overview', icon: 'overview' },
  { path: '/casinos', label: 'Casinos', icon: 'casinos' },
  { path: '/wallets', label: 'Wallets', icon: 'wallets' },
  { path: '/chains', label: 'Chains', icon: 'chains' },
  { path: '/compare', label: 'Compare', icon: 'compare' },
  { path: '/reports', label: 'Reports', icon: 'reports' },
  { path: '/trackers', label: 'Sources', icon: 'sources' },
] as const satisfies readonly { path: string; label: string; icon: IconName }[];
const ROUTES = ['/', '/casinos', '/casino', '/wallets', '/chains', '/compare', '/pair', '/matchup', '/reports', '/report', '/trackers'] as const;
type Route = (typeof ROUTES)[number];
const isRoute = (p: string): p is Route => (ROUTES as readonly string[]).includes(p);
const clean = (p: string) => p.replace(/\/+$/, '') || '/';

const WINDOWS: WindowKey[] = ['1d', '7d', '14d', '30d', '90d', '365d'];
const WLABEL: Record<WindowKey, string> = { '1d': '24h', '7d': '7d', '14d': '14d', '30d': '30d', '90d': '90d', '365d': '1y' };

function useRouter() {
  const read = () => {
    const p = clean(window.location.pathname);
    return { path: (isRoute(p) ? p : '/') as Route, search: new URLSearchParams(window.location.search) };
  };
  const [loc, setLoc] = useState(read);

  useEffect(() => {
    const pop = () => setLoc(read());
    window.addEventListener('popstate', pop);
    return () => window.removeEventListener('popstate', pop);
  }, []);

  const go = useCallback((href: string, keepScroll = false) => {
    window.history.pushState({}, '', new URL(href, window.location.origin));
    setLoc(read());
    if (!keepScroll) window.scrollTo({ top: 0 });
  }, []);

  return { ...loc, go };
}

export function App() {
  const { path, search, go } = useRouter();
  const [data, setData] = useState<Payload | null>(null);
  const [error, setError] = useState('');
  const header = useRef<HTMLElement>(null);
  useHeaderCondense(header);
  useReveal(`${path}?${search.toString()}|${data ? 'ready' : 'waiting'}`);

  const refresh = useCallback(() => {
    loadData(true).then((d) => { setData(d); setError(''); }, (e: unknown) => setError(String(e)));
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, 60_000);
    return () => clearInterval(timer);
  }, [refresh]);

  const win: WindowKey = data ? resolveWindow(data, search.get('w')) : '7d';
  const windows = data ? WINDOWS.filter((w) => data.analysis.volume[w]) : [];

  // Changing the window keeps you where you are: same page, same casino.
  const setWindow = (w: WindowKey) => {
    const next = new URLSearchParams(search);
    next.set('w', w);
    go(`${path}?${next.toString()}`, true);
  };

  // Every internal link is a plain <a>, so the site still works if this never runs.
  const onClick = (e: React.MouseEvent) => {
    const a = (e.target as HTMLElement).closest('a');
    if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || a.target === '_blank') return;
    const href = a.getAttribute('href');
    if (!href || !href.startsWith('/')) return;
    if (!isRoute(clean(new URL(href, window.location.origin).pathname))) return;
    e.preventDefault();
    go(href);
  };

  const stamp = data ? new Date(data.scrapedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
  const stale = data ? Date.now() - new Date(data.scrapedAt).getTime() > 7_200_000 : false;
  const active = path === '/casino' ? '/casinos' : path === '/report' ? '/reports' : path === '/matchup' || path === '/pair' ? '/compare' : path;

  return (
    <main className="container" onClick={onClick}>
      <div className="frame">
        <header className="dock top" ref={header}>
          <a className="brand" href={`/?w=${win}`}>
            <span className="brand-mark" aria-hidden><Mark tone="white" size={22} /></span>
            HouseGap
          </a>

          <nav className="dock-links" aria-label="Main">
            {NAV.map((r) => (
              <a key={r.path} href={`${r.path}?w=${win}`} aria-current={active === r.path ? 'page' : undefined} aria-label={r.label} title={r.label}>
                <Icon name={r.icon} />
                <span className="lbl">{r.label}</span>
              </a>
            ))}
          </nav>

          <div className="dock-actions">
            {windows.length > 1 && (
              <div className="seg seg-dark" role="group" aria-label="Time window">
                {windows.map((w) => <button key={w} aria-pressed={w === win} onClick={() => setWindow(w)}>{WLABEL[w]}</button>)}
              </div>
            )}
            <span className={`dock-status${stale ? ' down' : ''}`} title={data ? new Date(data.scrapedAt).toLocaleString('en-GB') : ''}>
              <i aria-hidden />{data ? stamp : '…'}
            </span>
          </div>
        </header>

        <div id="main">
          {error && !data && <div className="tile"><p className="empty">The collector is not answering. Start the API on port 8790.</p></div>}
          {!data && !error && <div className="tile"><p className="empty">Reading the trackers…</p></div>}
          {data && path === '/' && <Dashboard data={data} win={win} />}
          {data && path === '/casinos' && <Casinos data={data} win={win} search={search} />}
          {data && path === '/casino' && <CasinoProfile key={search.get('c') ?? ''} data={data} win={win} casinoKey={search.get('c') ?? ''} />}
          {data && path === '/wallets' && <Wallets data={data} win={win} />}
          {data && path === '/chains' && <Chains data={data} win={win} />}
          {data && path === '/compare' && <Compare data={data} win={win} mode="all" search={search} />}
          {data && path === '/pair' && <Compare data={data} win={win} mode="pair" search={search} />}
          {data && path === '/matchup' && <Compare data={data} win={win} mode="casinos" search={search} />}
          {data && path === '/reports' && <Reports data={data} win={win} />}
          {data && path === '/report' && <Report key={search.get('id') ?? ''} data={data} win={win} id={search.get('id') ?? ''} />}
          {data && path === '/trackers' && <Sources data={data} win={win} />}
        </div>
      </div>

      <footer className="site-foot">
        <span>HouseGap · four public trackers, cross-checked hourly</span>
        <span>Figures are what the trackers publish, not recommendations.</span>
      </footer>
    </main>
  );
}
